import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
  BackendPrice,
  CartRow,
  CartRowPrice,
  Customer,
  CustomerProductPrice,
  GungFlowService,
  NavigationConfig,
  PriceService,
  ProductService,
  SelectedCustomerService
} from 'gung-standard';
import { catchError, first, forkJoin, map, mergeMap, Observable, of, switchMap } from 'rxjs';
import { CarLinerMysetupService, MySetup } from './car-liner-mysetup.service';
import { JeevesPriceService } from 'gung-standard-jeeves';

@Injectable({
  providedIn: 'root'
})
export class CarLinerPriceService extends JeevesPriceService {
  constructor(
    http: HttpClient,
    customerService: SelectedCustomerService,
    productService: ProductService,
    gungFlowService: GungFlowService,
    protected carLinerMysetupService: CarLinerMysetupService,
    @Inject('environment')
    protected environment: NavigationConfig
  ) {
    super(http, customerService, productService, gungFlowService, environment);
  }

  getCustomerPrice(productId: string, customerId: string): Observable<CustomerProductPrice> {
    return this.internalHttpGetCustomerPrice(productId, customerId).pipe(
      mergeMap(x =>
        forkJoin([
          of(x),
          this.customerService.getSelectedCustomer().pipe(first()),
          this.carLinerMysetupService.getMySetup().pipe(first())
        ])
      ),
      map(([backendPrice, customer, mySetup]) => {
        return this.mapCustomerProductPriceWithSetup(backendPrice, customer, mySetup);
      })
    );
  }

  getCustomerPrices(productIds: string[], customerId: string): Observable<CustomerProductPrice[]> {
    return this.internalHttpGetCustomerPrices(productIds, customerId).pipe(
      switchMap(x =>
        forkJoin({
          backendPrices: of(x),
          customer: this.customerService.getSelectedCustomer().pipe(first()),
          mySetup: this.carLinerMysetupService.getMySetup().pipe(first())
        })
      ),
      map(({ backendPrices, customer, mySetup }) => {
        return Object.values(backendPrices).map(backendPrice => {
          return this.mapCustomerProductPriceWithSetup(backendPrice, customer, mySetup);
        });
      })
    );
  }

  protected mapCustomerProductPriceWithSetup(
    backendPrice: BackendPrice,
    customer: Customer,
    mySetup: MySetup,
    qty?: number
  ) {
    const priceLevel = this.getPriceLevel(backendPrice, qty);

    const customerDiscountPercent = priceLevel.discount;

    let customerGrossPrice = priceLevel.price ? priceLevel.price / ((100 - customerDiscountPercent) / 100) : 0;
    let customerNetPrice = priceLevel.price;
    if (mySetup && mySetup.currencyRate) {
      customerNetPrice = customerNetPrice * mySetup.currencyRate;
      customerGrossPrice = customerGrossPrice * mySetup.currencyRate;
    }

    if (mySetup && mySetup.markUp) {
      // customerNetPrice = customerNetPrice + customerNetPrice * (mySetup.markUp / 100);
      customerGrossPrice = customerGrossPrice + customerGrossPrice * (mySetup.markUp / 100);
    }

    let currencyCode = '';
    if (mySetup && mySetup.currency) {
      currencyCode = mySetup.currency;
    } else {
      currencyCode = customer.extra.kus.valkod;
    }

    const customerProductPrice: CustomerProductPrice = {
      productId: backendPrice.productId,
      customerId: customer.id,
      customerDiscountPercent,
      customerGrossPrice: {
        value: customerGrossPrice,
        currencyCode: currencyCode
      },
      customerNetPrice: {
        value: customerNetPrice,
        currencyCode: currencyCode
      },
      customerDiscountAmount: {
        value: customerGrossPrice - customerNetPrice,
        currencyCode: currencyCode
      },
      backendPriceLevel: priceLevel,
      priceFactor: backendPrice.priceFactor,
      backendPrice: backendPrice
    };

    return customerProductPrice;
  }

  getCustomerPricesWithQtys(
    input: { [productId: string]: number },
    customerId: string
  ): Observable<CustomerProductPrice[]> {
    return this.internalHttpGetCustomerPrices(Object.keys(input), customerId).pipe(
      switchMap(resp =>
        forkJoin({
          prices: of(resp),
          customer: this.customerService.getSelectedCustomer().pipe(first()),
          mySetup: this.carLinerMysetupService.getMySetup().pipe(first())
        })
      ),
      map(({ prices, customer, mySetup }) => {
        return Object.values(prices).map(price =>
          this.mapCustomerProductPriceWithSetup(price, customer, mySetup, input[price.productId])
        );
      })
    );
  }

  priceMarkupIsEnabled(): Observable<boolean> {
    return this.carLinerMysetupService.getMySetup().pipe(
      first(),
      map(mySetup => !!(mySetup && mySetup.markUp && mySetup.markUp > 0))
    );
  }

}
